/**
 * 通用 Panel 组件——逐结构对齐旧 `com.nuclearunicorn.game.ui.Panel`
 *（core.js:2497-2603）。
 *
 * 用于中栏 tab 的折叠面板（Workshop 的 upgrade/craft 区、Trade 的 race 面板等）。
 * - 外层 `div.panelContainer`；
 * - 右上角 `div.toggle`（折叠态显示 "+"，展开显示 "-"，可键盘 Enter 切换）；
 * - `div.title`（innerHTML 语义，支持 RacePanel 的 attitude span）；
 * - `div.container` 包裹子内容。
 *
 * 折叠态默认组件内 state；RacePanel 等需要受控时通过 `collapsed` + `onToggle`。
 */
import { useCallback, useState, type KeyboardEvent, type ReactNode } from "react";

export interface PanelProps {
  /** 面板标题（innerHTML 语义） */
  title: string;
  /** 受控折叠态；不传时为非受控 */
  collapsed?: boolean;
  /** 初始折叠态（仅非受控有效） */
  initialCollapsed?: boolean;
  /** 折叠切换回调 */
  onToggle?: (collapsed: boolean) => void;
  children?: ReactNode;
}

export function Panel({
  title,
  collapsed: controlledCollapsed,
  initialCollapsed = false,
  onToggle,
  children,
}: PanelProps) {
  const [internalCollapsed, setInternalCollapsed] = useState(initialCollapsed);
  const isControlled = controlledCollapsed !== undefined;
  const collapsed = isControlled ? controlledCollapsed : internalCollapsed;

  const toggle = useCallback(() => {
    const next = !collapsed;
    if (!isControlled) {
      setInternalCollapsed(next);
    }
    onToggle?.(next);
  }, [collapsed, isControlled, onToggle]);

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        toggle();
      }
    },
    [toggle],
  );

  return (
    <div className="panelContainer">
      <div
        className={"toggle" + (collapsed ? " collapsed" : "")}
        style={{ float: "right" }}
        tabIndex={0}
        onClick={toggle}
        onKeyPress={handleKeyPress}
      >
        {collapsed ? "+" : "-"}
      </div>
      <div className="title" dangerouslySetInnerHTML={{ __html: title }} />
      <div className="container" style={{ display: collapsed ? "none" : "" }}>
        {children}
      </div>
    </div>
  );
}
